import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { toast } from 'sonner';
import { Clock, BarChart3, CheckCircle, XCircle, Share2, Users, TrendingUp, AlertTriangle } from 'lucide-react';
import { Card, CardTitle } from '../ui/card';
import BoostBadge, { isMarketBoosted } from './BoostBadge';
import ShareMenu from './ShareMenu';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { useAuth } from '../../contexts';
import { hapticFeedback } from '../../utils';

// Mirror of GET /api/markets/<id>/odds. The endpoint is just math over
// the same outcome rows we already fetch in the markets list, so we
// derive locally to skip ~100 extra HTTP round-trips per page-load.
// Falls back to a 50/50 shape if pools are missing.
const deriveOddsFromOutcomes = (outcomes) => {
  if (!outcomes || outcomes.length === 0) return null;
  const primary = outcomes[0];
  const poolYes = Number(primary.pool_yes ?? 0);
  const poolNo = Number(primary.pool_no ?? 0);
  const total = poolYes + poolNo;
  // The Mongo era used pool_no / total as YES price (Parimutuel
  // mirror — backing the underdog pays more). Keep the same formula.
  const yesPrice = total > 0 ? poolNo / total : 0.5;
  const noPrice = 1 - yesPrice;
  const yesOdds = yesPrice > 0 ? 1 / yesPrice : 2.0;
  const noOdds = noPrice > 0 ? 1 / noPrice : 2.0;
  const yesOutcomeId = (outcomes.find(o => o.label === 'YES') || primary).id;
  const noOutcomeId = (outcomes.find(o => o.label === 'NO') || outcomes[1] || primary).id;
  return {
    market_id: primary.market_id,
    outcome_id: primary.id,
    outcome_label: primary.label,
    yes_price: yesPrice,
    no_price: noPrice,
    yes_odds: yesOdds,
    no_odds: noOdds,
    pool_yes: poolYes,
    pool_no: poolNo,
    yes_outcome_id: yesOutcomeId,
    no_outcome_id: noOutcomeId,
  };
};

const MarketCard = ({ market, outcomes = [], friendsCount: friendsCountProp, onBetPlaced }) => {
  const { t } = useTranslation();
  const [showBetDialog, setShowBetDialog] = useState(false);
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [betAmount, setBetAmount] = useState('');
  const [bettingYes, setBettingYes] = useState(true);
  const [marketOdds, setMarketOdds] = useState(null);
  const [betQuote, setBetQuote] = useState(null);
  const [priceHistory, setPriceHistory] = useState(null);
  const [showPriceChart, setShowPriceChart] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  // friendsCount can come from the parent (markets list pre-fetches a
  // bulk count map) — fall back to a local fetch only when the prop is
  // missing (e.g. card rendered standalone outside the markets list).
  const [friendsCountLocal, setFriendsCountLocal] = useState(0);
  const [isPlacing, setIsPlacing] = useState(false);
  const placingRef = useRef(false);
  const friendsCount = friendsCountProp ?? friendsCountLocal;
  const { user, isAdmin, credits = 0, refreshCredits, fetchNotifications, playNotificationSound } = useAuth();

  // Markets list pre-fetches all outcomes in one bulk call and passes
  // them via the `outcomes` prop. Computing yes/no prices + odds + pools
  // locally from that data avoids one extra GET /markets/<id>/odds per
  // card, which otherwise multiplies into ~100 requests per page-load.
  // After a successful bet we still call fetchMarketOdds to refresh
  // the pools (pool changes mid-session aren't reflected in the
  // outcomes prop until the parent reloads).
  useEffect(() => {
    if (outcomes && outcomes.length > 0) {
      setMarketOdds(deriveOddsFromOutcomes(outcomes));
    } else if (market.mode !== 'civic') {
      // Civic-Märkte laufen über yes_votes/no_votes und haben keine
      // Parimutuel-Outcomes -> GET /odds würde 404 liefern. Nur für
      // Prediction-Märkte (mit Outcomes) die Quoten nachladen.
      fetchMarketOdds();
    }
    // Only do the per-market activity fetch if the parent didn't already
    // provide a count via the bulk endpoint.
    if (user && friendsCountProp == null) fetchFriendsActivity();
  }, [market.id, outcomes, user?.id, friendsCountProp]);

  // Debounced quote fetch while the user types a bet amount. Uses the
  // existing /bets/quote endpoint (which reports slippage_pct) so the
  // dialog can warn about large-bet slippage before the user submits.
  useEffect(() => {
    if (!showBetDialog || !selectedOutcome || !betAmount) {
      setBetQuote(null);
      return;
    }
    const stake = parseFloat(betAmount);
    if (!stake || stake <= 0) {
      setBetQuote(null);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.bets.quote({
          type: 'single',
          legs: [{ market_id: market.id, outcome_id: selectedOutcome.id, betting_yes: bettingYes }],
          stake,
        });
        setBetQuote(data);
      } catch {
        setBetQuote(null);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [showBetDialog, selectedOutcome, betAmount, bettingYes, market.id]);

  const fetchFriendsActivity = async () => {
    try {
      const res = await api.friends.getActivity(market.id);
      setFriendsCountLocal(res.data.count || 0);
    } catch {}
  };

  const fetchMarketOdds = async () => {
    try {
      const response = await api.markets.getOdds(market.id);
      setMarketOdds(response.data);
    } catch (error) {
      // 404 = Markt hat (noch) keine Outcomes (z.B. Civic) -> erwarteter
      // Fall, kein echter Fehler. Nur unerwartete Fehler loggen.
      if (error?.response?.status !== 404) {
        console.error('Error fetching market odds:', error);
      }
    }
  };

  const fetchPriceHistory = async () => {
    try {
      const response = await api.markets.getPriceHistory(market.id, { days: 30 });
      // Backend records snapshots only against the primary outcome
      // (always labeled "YES"), so the response.NO series is always
      // empty even on markets that took NO bets. In a parimutuel
      // market NO_price = 1 - YES_price by construction, so we derive
      // the NO candle row-by-row from the YES candle when NO is
      // empty. open/close flip directly; high/low swap (when YES
      // low, NO high) so the candle wicks stay correct.
      const yesSeries = (response.data && response.data.YES) || [];
      const noSeries = (response.data && response.data.NO) || [];
      const derived = {
        YES: yesSeries,
        NO: noSeries.length > 0 ? noSeries : yesSeries.map((c) => ({
          ...c,
          open: 1 - (c.open ?? c.close ?? 0.5),
          close: 1 - (c.close ?? 0.5),
          high: 1 - (c.low ?? c.close ?? 0.5),
          low: 1 - (c.high ?? c.close ?? 0.5),
        })),
      };
      setPriceHistory(derived);
      setShowPriceChart(true);
    } catch (error) {
      console.error('Error fetching price history:', error);
    }
  };

  const getTimeUntilClose = () => {
    const now = new Date();
    const closeTime = new Date(market.closes_at);
    const diff = closeTime - now;
    
    if (diff <= 0) return 'Closed';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h`;
    return `${hours}h`;
  };

  const handleBet = (outcome) => {
    if (!user) {
      toast.error('Please login to place bets');
      return;
    }

    // Admins are operators, not players. The backend rejects their
    // place_bet calls; mirror that here so they get an immediate
    // explanation instead of a 403 toast.
    if (isAdmin) {
      hapticFeedback('error');
      toast.error('Admin accounts cannot place bets', {
        description: 'Create a separate user account to test betting.',
        duration: 4000,
      });
      return;
    }

    if (getTimeUntilClose() === 'Closed' || market.status === 'closed' || market.status === 'settled') {
      hapticFeedback('error');
      toast.error('This market is closed for betting', {
        description: market.status === 'settled' ? 'This market has already been settled.' : 'The betting period has ended.',
        duration: 4000
      });
      return;
    }
    
    hapticFeedback('light');
    setSelectedOutcome(outcome);
    setShowBetDialog(true);
  };

  const placeBet = async () => {
    if (!betAmount || !selectedOutcome || !marketOdds) return;
    // In-Flight-Guard: verhindert, dass ein zweiter Klick einen
    // weiteren Request mit frischem Key absetzt, bevor der erste
    // durch ist. Der Idempotency-Key sichert zusaetzlich gegen
    // Netzwerk-Retries ab.
    if (placingRef.current) return;
    placingRef.current = true;
    setIsPlacing(true);

    try {
      const quoteData = {
        type: 'single',
        legs: [{
          market_id: market.id,
          outcome_id: selectedOutcome.id,
          betting_yes: bettingYes
        }],
        stake: parseFloat(betAmount)
      };

      const idempotencyKey =
        (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `${market.id}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const quoteResponse = await api.bets.quote(quoteData);
      await api.bets.place(quoteData, idempotencyKey);

      const direction = bettingYes ? 'YES' : 'NO';
      
      hapticFeedback('success');
      if (playNotificationSound) playNotificationSound();
      
      toast.success(`${direction} bet placed successfully!`);
      
      refreshCredits();
      if (onBetPlaced) onBetPlaced();
      if (fetchNotifications) fetchNotifications();
      
      setShowBetDialog(false);
      setBetAmount('');
      fetchMarketOdds();
      
    } catch (error) {
      console.error('Error placing bet:', error);
      toast.error('Failed to place bet: ' + (error.response?.data?.detail || error.message));
    } finally {
      placingRef.current = false;
      setIsPlacing(false);
    }
  };

  const isClosed = getTimeUntilClose() === 'Closed' || market.status === 'closed' || market.status === 'settled';
  const isBoosted = isMarketBoosted(market);

  return (
    <Card className={`group hover:shadow-lg dark:hover:shadow-black/40 transition-all duration-300 relative overflow-hidden dark:bg-slate-900/80 dark:backdrop-blur-sm ${
      isBoosted
        ? 'border-orange-300 ring-1 ring-orange-300/60 shadow-orange-500/10 shadow-lg dark:border-orange-500/40 dark:ring-orange-500/30'
        : 'border-gray-200 dark:border-slate-800 dark:border-slate-700'
    }`}>
      <div className="p-4 space-y-3">
        {/* Header-Reihe: optionales Thumbnail + Titel + Inline-Meta.
            Statt zwei voller Spalten ist das Bild jetzt klein (h-14)
            neben dem Titel, Meta wandert in eine einzelne dichte Zeile. */}
        <div className="flex gap-3">
          {market.image_url && (
            <div className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700">
              <img
                src={market.image_url}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base sm:text-lg leading-snug line-clamp-2">
              {/* Echter Link statt div-onClick: tastatur-fokussierbar,
                  Rechtsklick "in neuem Tab", Screenreader-konform. Das
                  Ziel /markets/:id scrollt zur Card und hebt sie hervor
                  (Deep-Link-Handler in MarketsPage). */}
              <Link
                to={`/markets/${market.id}`}
                className="text-gray-900 dark:text-gray-100 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
              >
                {market.title}
              </Link>
            </CardTitle>
            {/* Inline-Meta: Prediction-Badge, Kategorie, Restzeit.
                Settled-Badge ersetzt Restzeit wenn der Markt entschieden. */}
            <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mt-1.5 text-xs">
              {isBoosted && <BoostBadge />}
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300 text-[10px] font-bold uppercase tracking-wide dark:text-emerald-400">
                Prediction
              </span>
              <span className="text-gray-500 dark:text-gray-400">{market.category}</span>
              {market.status === 'settled' && market.winning_outcome_label ? (
                <Badge
                  variant="outline"
                  className={`text-[10px] ml-auto ${
                    market.winning_outcome_label === 'YES'
                      ? 'text-emerald-700 dark:text-emerald-300 border-emerald-600 bg-emerald-50 dark:bg-emerald-500/10'
                      : market.winning_outcome_label === 'NO'
                      ? 'text-rose-700 dark:text-rose-300 border-rose-600 bg-rose-50 dark:bg-rose-500/10'
                      : 'text-blue-600 border-blue-600'
                  }`}
                  data-testid={`market-settled-badge-${market.id}`}
                >
                  Settled: {market.winning_outcome_label}
                </Badge>
              ) : (
                <div className={`ml-auto inline-flex items-center gap-1 ${isClosed ? 'text-rose-600 dark:text-rose-400 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                  <Clock className="h-3 w-3" />
                  {getTimeUntilClose()}
                </div>
              )}
            </div>
            {market.description && (
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1.5 line-clamp-2">
                {market.description}
              </p>
            )}
          </div>
        </div>

        {/* Bet-Action: YES/NO als zwei gleich breite, prominent
            klickbare Bloecke. Klare Hierarchie -- Probability gross,
            Quote klein darunter. Disabled-State wenn Market closed. */}
        {marketOdds && (
          <div className={`grid grid-cols-2 gap-2 ${isClosed ? 'opacity-60' : ''}`}>
            <button
              type="button"
              disabled={isClosed}
              className={`group/yes flex flex-col items-start px-3 py-2.5 rounded-lg border transition-all ${
                isClosed
                  ? 'bg-gray-50 dark:bg-slate-800/50 border-gray-200 dark:border-slate-700 cursor-not-allowed'
                  : 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 hover:border-emerald-300 dark:hover:border-emerald-500/50 cursor-pointer'
              }`}
              onClick={() => {
                setBettingYes(true);
                handleBet(outcomes.find(o => o.label === 'YES') || outcomes[0] || { id: marketOdds.yes_outcome_id, label: 'YES', price: marketOdds.yes_price });
              }}
            >
              <div className="flex items-baseline justify-between w-full">
                <span className={`text-xs font-bold tracking-wide ${isClosed ? 'text-gray-500 dark:text-gray-400' : 'text-emerald-700 dark:text-emerald-300'}`}>
                  YES
                </span>
                <span className={`text-lg font-bold ${isClosed ? 'text-gray-500 dark:text-gray-400' : 'text-emerald-800 dark:text-emerald-200'}`}>
                  {(marketOdds.yes_price * 100).toFixed(0)}%
                </span>
              </div>
              <span className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                {marketOdds.yes_odds.toFixed(2)}x Quote
              </span>
            </button>
            <button
              type="button"
              disabled={isClosed}
              className={`group/no flex flex-col items-start px-3 py-2.5 rounded-lg border transition-all ${
                isClosed
                  ? 'bg-gray-50 dark:bg-slate-800/50 border-gray-200 dark:border-slate-700 cursor-not-allowed'
                  : 'bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/30 hover:bg-rose-100 dark:hover:bg-rose-500/20 hover:border-rose-300 dark:hover:border-rose-500/50 cursor-pointer'
              }`}
              onClick={() => {
                setBettingYes(false);
                handleBet(outcomes.find(o => o.label === 'NO') || outcomes[1] || { id: marketOdds.no_outcome_id, label: 'NO', price: marketOdds.no_price });
              }}
            >
              <div className="flex items-baseline justify-between w-full">
                <span className={`text-xs font-bold tracking-wide ${isClosed ? 'text-gray-500 dark:text-gray-400' : 'text-rose-700 dark:text-rose-300'}`}>
                  NO
                </span>
                <span className={`text-lg font-bold ${isClosed ? 'text-gray-500 dark:text-gray-400' : 'text-rose-800 dark:text-rose-200'}`}>
                  {(marketOdds.no_price * 100).toFixed(0)}%
                </span>
              </div>
              <span className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                {marketOdds.no_odds.toFixed(2)}x Quote
              </span>
            </button>
          </div>
        )}

        {/* Footer: Pool + Friends-Activity links, Chart/Share-Icons rechts.
            Komprimiert auf eine Zeile, deutlich weniger Visual-Noise als
            das vorherige 3-Spalten-Grid. */}
        <div className="flex items-center justify-between gap-2 pt-3 border-t border-gray-100 dark:border-slate-800 text-[11px] text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            {marketOdds && (
              <span>Pool {(marketOdds.pool_yes + marketOdds.pool_no).toFixed(0)} VC</span>
            )}
            {friendsCount > 0 && (
              <span className="text-blue-600 dark:text-blue-400 inline-flex items-center gap-1" data-testid={`friends-activity-${market.id}`}>
                <Users className="h-3 w-3" />
                {friendsCount === 1 ? '1 Freund' : `${friendsCount} Freunde`}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); fetchPriceHistory(); }}
              className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
              aria-label="Chart anzeigen"
              title="Chart"
            >
              <BarChart3 size={14} />
            </button>
            <div className="relative">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShowShareMenu(!showShareMenu); }}
                className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-emerald-500 dark:hover:text-emerald-400 transition-colors"
                data-testid={`share-btn-${market.id}`}
                aria-label="Teilen"
                title="Teilen"
              >
                <Share2 size={14} />
              </button>
              {showShareMenu && (
                <ShareMenu
                  text={`${market.title} — YES ${marketOdds ? (marketOdds.yes_price * 100).toFixed(0) : '50'}%`}
                  url={`${window.location.origin}/markets${user ? '?ref=' + user.handle : ''}`}
                  onClose={() => setShowShareMenu(false)}
                />
              )}
            </div>
          </div>
        </div>

        {/* Fallback: Marktdaten noch nicht geladen -- primitive Outcome-
            Liste fuer direkten Bet-Klick. Render-Pfad nur wenn marketOdds
            null ist (uebliche Race-Condition zwischen List-Fetch und
            Detail-Hydration). */}
        {!marketOdds && outcomes.length > 0 && (
          <div className="space-y-2 pt-3 border-t border-gray-100 dark:border-slate-800">
            {outcomes.map((outcome) => (
              <div
                key={outcome.id}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-800 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors cursor-pointer dark:bg-slate-800/60 dark:hover:bg-slate-800"
                onClick={() => handleBet(outcome)}
              >
                <span className="font-medium text-gray-900 dark:text-gray-100">{outcome.label}</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                    {Number(outcome.price ?? 0).toFixed(2)} VC
                  </span>
                  <Button size="sm" variant="outline">Bet</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bet Dialog -- redesigned:
          - Header zeigt klar welches Outcome + aktueller %-Wert
          - Slider + Number-Input synced, Quick-Buttons fuer haeufige
            Einsaetze (10/25/50/100/max)
          - Quote-Panel zeigt Probability/Odds/Payout/Gewinn live,
            verwendet die parimutuel-Estimate aus betQuote sobald
            verfuegbar (sonst naive odds-Hochrechnung)
          - Slippage-Warning prominent bei >5%
          - Color-coded CTA mit Betrag im Button-Text
          - Dark Mode durchgehend */}
      <Dialog open={showBetDialog} onOpenChange={setShowBetDialog}>
        <DialogContent className="dark:bg-slate-900 dark:border-slate-800">
          {(() => {
            const stake = parseFloat(betAmount) || 0;
            const naivePayout = marketOdds
              ? stake * (bettingYes ? marketOdds.yes_odds : marketOdds.no_odds)
              : 0;
            const estPayout = betQuote?.parimutuel?.estimated_payout != null
              ? Number(betQuote.parimutuel.estimated_payout)
              : naivePayout;
            const profit = estPayout - stake;
            const slippagePct = betQuote?.parimutuel?.slippage_pct
              ? Number(betQuote.parimutuel.slippage_pct)
              : 0;
            const probabilityPct = marketOdds
              ? (bettingYes ? marketOdds.yes_price : marketOdds.no_price) * 100
              : 0;
            const odds = marketOdds
              ? (bettingYes ? marketOdds.yes_odds : marketOdds.no_odds)
              : 0;
            // Pool-Cap aus dem 20%-min-Pool-Limit (Backend enforced) --
            // verhindert dass der Bet beim Submit mit 400 zurueckkommt.
            // Bei brandneuen Markets (pool=0) gibt es noch keinen Cap aus
            // den Outcomes, dann fallen wir auf das Credits-Limit zurueck.
            const poolSum = marketOdds ? (marketOdds.pool_yes || 0) + (marketOdds.pool_no || 0) : 0;
            const marketMaxBet = poolSum > 0
              ? Math.floor(Math.min(marketOdds.pool_yes, marketOdds.pool_no) * 0.20)
              : null;
            const creditsMax = Math.max(0, Math.floor(credits));
            const maxStake = marketMaxBet != null
              ? Math.max(0, Math.min(creditsMax, marketMaxBet))
              : creditsMax;
            const limitReason = marketMaxBet != null && marketMaxBet < creditsMax
              ? 'market'
              : 'credits';
            const sliderValue = Math.min(stake, maxStake);
            const overLimit = stake > maxStake;
            const sideColor = bettingYes
              ? 'text-emerald-700 dark:text-emerald-400'
              : 'text-rose-700 dark:text-rose-400';
            const sideBg = bettingYes
              ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30'
              : 'bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/30';
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="text-left dark:text-gray-100 line-clamp-2 pr-6">
                    {market.title}
                  </DialogTitle>
                  <DialogDescription className="text-left dark:text-gray-400">
                    Du tippst auf{' '}
                    <span className={`font-bold ${sideColor}`}>
                      {bettingYes ? 'JA' : 'NEIN'}
                    </span>{' '}
                    bei aktuell {probabilityPct.toFixed(1)}% · Quote {odds.toFixed(2)}x
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  {/* Outcome-Switch im Dialog (selten genutzt aber praktisch
                      wenn der User vom YES-Card-Klick zur NO-Side wechseln
                      will, ohne den Dialog zu schliessen). */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setBettingYes(true)}
                      className={`px-3 py-2 rounded-lg border text-sm font-bold transition-all ${
                        bettingYes
                          ? 'bg-emerald-50 dark:bg-emerald-500/15 border-emerald-300 dark:border-emerald-500/40 text-emerald-700 dark:text-emerald-300 ring-2 ring-emerald-200 dark:ring-emerald-500/30'
                          : 'bg-gray-50 dark:bg-slate-800/60 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:border-emerald-200 dark:hover:border-emerald-500/40'
                      }`}
                    >
                      JA · {marketOdds ? (marketOdds.yes_price * 100).toFixed(0) : '50'}%
                    </button>
                    <button
                      type="button"
                      onClick={() => setBettingYes(false)}
                      className={`px-3 py-2 rounded-lg border text-sm font-bold transition-all ${
                        !bettingYes
                          ? 'bg-rose-50 dark:bg-rose-500/15 border-rose-300 dark:border-rose-500/40 text-rose-700 dark:text-rose-300 ring-2 ring-rose-200 dark:ring-rose-500/30'
                          : 'bg-gray-50 dark:bg-slate-800/60 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:border-rose-200 dark:hover:border-rose-500/40'
                      }`}
                    >
                      NEIN · {marketOdds ? (marketOdds.no_price * 100).toFixed(0) : '50'}%
                    </button>
                  </div>

                  {/* Stake-Auswahl: Slider + Number-Input + Quick-Buttons.
                      Slider und Input teilen denselben State; Slider klemmt
                      auf maxStake (= verfuegbare Credits). */}
                  <div className="space-y-3">
                    <div className="flex items-baseline justify-between">
                      <Label className="text-sm dark:text-gray-300">Einsatz</Label>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {limitReason === 'market'
                          ? <>Max <span className="font-semibold tabular-nums">{maxStake.toLocaleString('de-DE')}</span> VC <span className="opacity-70">(20% Pool-Cap)</span></>
                          : <><span className="font-semibold tabular-nums">{creditsMax.toLocaleString('de-DE')}</span> VC verfügbar</>
                        }
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        placeholder="0"
                        min="0"
                        max={maxStake}
                        value={betAmount}
                        onChange={(e) => setBetAmount(e.target.value)}
                        className="w-28 text-lg font-bold text-center dark:bg-slate-800 dark:border-slate-700 dark:text-gray-100"
                      />
                      <span className="text-sm text-gray-500 dark:text-gray-400 font-medium">VC</span>
                      <div className="flex-1 min-w-0">
                        <Slider
                          value={[sliderValue]}
                          max={maxStake || 100}
                          step={1}
                          onValueChange={(v) => setBetAmount(String(v[0] ?? 0))}
                        />
                      </div>
                    </div>
                    {overLimit && (
                      <p className="text-xs text-rose-600 dark:text-rose-400 font-medium">
                        {limitReason === 'market'
                          ? `Max ${maxStake} VC für diesen Markt (20% Pool-Cap)`
                          : `Du hast nur ${creditsMax} VC zur Verfügung`}
                      </p>
                    )}
                    <div className="flex gap-2 flex-wrap">
                      {[10, 25, 50, 100].filter(v => v <= maxStake).map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => setBetAmount(String(v))}
                          className="px-3 py-1 text-xs font-medium rounded-md border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800/60 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors dark:bg-slate-900/80 dark:hover:bg-slate-800/60"
                        >
                          {v} VC
                        </button>
                      ))}
                      {maxStake > 0 && (
                        <button
                          type="button"
                          onClick={() => setBetAmount(String(maxStake))}
                          className="px-3 py-1 text-xs font-medium rounded-md border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800/60 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors dark:bg-slate-900/80 dark:hover:bg-slate-800/60"
                        >
                          Max
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Quote-Panel: zeigt geschaetzte Auszahlung + Gewinn.
                      Side-getoent (emerald fuer JA, rose fuer NEIN), damit
                      visuell klar bleibt welche Wette gerade berechnet wird. */}
                  {marketOdds && stake > 0 && (
                    <div className={`p-3 rounded-lg border ${sideBg}`}>
                      <div className="flex justify-between items-baseline mb-1">
                        <span className="text-xs text-gray-600 dark:text-gray-400">Geschätzte Auszahlung</span>
                        <span className={`text-2xl font-bold ${sideColor} tabular-nums`}>
                          {estPayout.toFixed(2)} VC
                        </span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                        <span>Möglicher Gewinn</span>
                        <span className={`font-semibold tabular-nums ${profit >= 0 ? sideColor : ''}`}>
                          {profit >= 0 ? '+' : ''}{profit.toFixed(2)} VC
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-500 dark:text-gray-500 mt-2 leading-snug dark:text-gray-400">
                        Parimutuel: Die endgültige Auszahlung richtet sich nach dem Pool bei Marktabschluss.
                      </p>
                    </div>
                  )}

                  {/* Slippage-Warning bei >5% -- prominent in amber/orange */}
                  {slippagePct > 5 && (
                    <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/30 p-3 rounded-lg" role="alert">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-semibold text-amber-800 dark:text-amber-200">Großer Einsatz · Slippage</p>
                          <p className="text-amber-700 dark:text-amber-300 mt-0.5">
                            Dieser Einsatz verschiebt den Pool um {slippagePct.toFixed(1)}%.
                            Tatsächliche Auszahlung{' '}
                            <span className="font-semibold tabular-nums">{Number(betQuote.parimutuel.estimated_payout).toFixed(2)}</span>{' '}
                            statt naiver Schätzung{' '}
                            <span className="font-semibold tabular-nums">{Number(betQuote.parimutuel.naive_payout).toFixed(2)}</span>.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* CTA: gross, color-coded, mit Betrag im Text */}
                  <div className="flex gap-2 pt-1">
                    <Button
                      type="button"
                      onClick={placeBet}
                      disabled={!stake || stake > maxStake || isPlacing}
                      size="lg"
                      className={`flex-1 text-base font-bold dark:!bg-none ${
                        bettingYes
                          ? 'bg-emerald-600 hover:bg-emerald-700 dark:!bg-emerald-600 dark:hover:!bg-emerald-500 dark:!text-white'
                          : 'bg-rose-600 hover:bg-rose-700 dark:!bg-rose-600 dark:hover:!bg-rose-500 dark:!text-white'
                      }`}
                    >
                      {stake > 0
                        ? `${stake.toFixed(0)} VC auf ${bettingYes ? 'JA' : 'NEIN'}`
                        : `Einsatz wählen`}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="lg"
                      onClick={() => setShowBetDialog(false)}
                      className="dark:border-slate-700 dark:text-gray-300 dark:hover:bg-slate-800"
                    >
                      {t('common.cancel')}
                    </Button>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Price History Chart Dialog */}
      <Dialog open={showPriceChart} onOpenChange={setShowPriceChart}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pr-6">
            <DialogTitle className="text-base sm:text-lg leading-tight text-left">
              <span className="line-clamp-2">Price History - {market.title}</span>
            </DialogTitle>
            <DialogDescription className="text-sm text-left">30-day price movement</DialogDescription>
          </DialogHeader>
          
          {priceHistory && (() => {
            // recharts AreaChart needs at least 2 points to draw a line
            // or area. When we only have one daily candle yet (the
            // common case for fresh markets), the chart looks empty
            // even though data exists. Render a single-point view
            // with the latest price as a label + a dot, and switch to
            // the full area chart only at length >= 2.
            const renderSeries = (label, data, color, gradientId, Icon, accentClass) => {
              const latest = data && data.length > 0 ? data[data.length - 1] : null;
              const latestPct = latest ? (latest.close * 100).toFixed(1) : null;
              return (
                <div>
                  <h4 className={`font-semibold mb-2 sm:mb-3 ${accentClass} flex items-center gap-2 text-sm sm:text-base`}>
                    <Icon className="h-4 w-4" />
                    {label} Price Movement
                    {latestPct && (
                      <span className="ml-auto text-xs font-mono font-bold">
                        {latestPct}%
                      </span>
                    )}
                  </h4>
                  {data && data.length > 1 ? (
                    <ResponsiveContainer width="100%" height={150}>
                      <AreaChart data={data}>
                        <defs>
                          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={color} stopOpacity={0.3}/>
                            <stop offset="95%" stopColor={color} stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(val) => val.slice(5)} />
                        <YAxis
                          domain={[0, 1]}
                          tick={{ fontSize: 10 }}
                          tickFormatter={(val) => `${(val * 100).toFixed(0)}%`}
                        />
                        <Tooltip
                          formatter={(value) => [`${(value * 100).toFixed(1)}%`, 'Price']}
                          labelFormatter={(lbl) => `Date: ${lbl}`}
                        />
                        <Area
                          type="monotone"
                          dataKey="close"
                          stroke={color}
                          fill={`url(#${gradientId})`}
                          strokeWidth={2}
                          dot={{ r: 3, stroke: color, fill: color }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : data && data.length === 1 ? (
                    <div className="h-32 flex flex-col items-center justify-center text-gray-600 bg-gray-50 rounded-lg gap-1 dark:bg-slate-800/60 dark:text-gray-400">
                      <div className="text-3xl font-bold" style={{ color }}>
                        {latestPct}%
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {latest.date} — only one candle yet
                      </div>
                    </div>
                  ) : (
                    <div className="h-32 flex items-center justify-center text-gray-500 bg-gray-50 rounded-lg dark:bg-slate-800/60 dark:text-gray-400">
                      No {label} price data available
                    </div>
                  )}
                </div>
              );
            };
            return (
              <div className="space-y-4 sm:space-y-6">
                {renderSeries('YES', priceHistory.YES, '#10b981', 'yesGradient', CheckCircle, 'text-green-700')}
                {renderSeries('NO', priceHistory.NO, '#ef4444', 'noGradient', XCircle, 'text-red-700')}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* 0 Credits Modal */}
      <Dialog open={showCreditsModal} onOpenChange={setShowCreditsModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Credits erforderlich</DialogTitle>
            <DialogDescription>
              Für Prediction-Märkte benötigst du Credits.
              Kaufe Credits im Shop oder verdiene sie durch aktives Mitspielen auf der Plattform.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button className="flex-1" onClick={() => { setShowCreditsModal(false); window.location.href = '/shop'; }}>
              Credits kaufen
            </Button>
            <Button variant="outline" className="flex-1" onClick={() => setShowCreditsModal(false)}>
              Schließen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default MarketCard;
